/**
 * The catalog-driven entitlements form — ONE editor for the two surfaces that set them:
 * a PLAN (the template) and a per-licence CUSTOMISATION of that template.
 *
 * The axes live in backend code, so the form is generated from `/entitlements-catalog`
 * and a new axis appears after a deploy with no FE change. Two things it does that a
 * hand-rolled second copy would have got wrong:
 *
 * - A `fallback: "global"` limit renders "= globale: N" when empty, NOT "∞" — those two
 *   concurrency caps inherit the platform default, and calling that unlimited is a lie
 *   the operator would act on (ADR-0040).
 * - With a `baseline` (the plan being customised) each changed axis is MARKED and can be
 *   reset, so "what did I deviate from the plan on?" is answerable at a glance instead of
 *   by diffing two screens.
 */
import { RotateCcw } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import type { EntitlementsCatalog, PlanEntitlements } from "@/lib/platform";

export function EntitlementsEditor({
  catalog,
  value,
  onChange,
  baseline,
  globalCap,
  featuresLegend = "Funzionalità",
  limitsLegend = "Limiti (vuoto = illimitato, salvo dove indicato)",
}: {
  catalog: EntitlementsCatalog;
  value: PlanEntitlements;
  onChange: (next: PlanEntitlements) => void;
  /** The plan being customised — when set, deviations are marked + resettable. */
  baseline?: PlanEntitlements | null;
  /** Global default for a `fallback:"global"` axis, so an empty field reads honestly. */
  globalCap?: (key: string) => number | undefined;
  featuresLegend?: string;
  limitsLegend?: string;
}) {
  const setFeature = (k: string, v: boolean) =>
    onChange({ ...value, features: { ...value.features, [k]: v } });
  const setLimit = (k: string, raw: string) =>
    onChange({
      ...value,
      limits: { ...value.limits, [k]: raw.trim() === "" ? null : Math.max(0, Number(raw)) },
    });

  const featureChanged = (k: string) =>
    !!baseline && Boolean(value.features[k]) !== Boolean(baseline.features?.[k]);
  const limitChanged = (k: string) =>
    !!baseline && (value.limits[k] ?? null) !== (baseline.limits?.[k] ?? null);

  return (
    <>
      <fieldset className="space-y-2 rounded-md border border-border/60 p-3">
        <legend className="px-1 text-xs font-medium text-muted-foreground">{featuresLegend}</legend>
        {catalog.features.map((a) => (
          <label key={a.key} className="flex items-center justify-between gap-2 text-sm">
            <span title={a.description} className={cn(featureChanged(a.key) && "font-medium")}>
              {a.label}
              {featureChanged(a.key) && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-warning">
                  modificato
                </span>
              )}
            </span>
            <div className="flex items-center gap-1.5">
              {featureChanged(a.key) && (
                <ResetButton
                  label={a.label}
                  onClick={() => setFeature(a.key, Boolean(baseline?.features?.[a.key]))}
                />
              )}
              <input
                type="checkbox"
                aria-label={a.label}
                checked={Boolean(value.features[a.key])}
                onChange={(e) => setFeature(a.key, e.target.checked)}
              />
            </div>
          </label>
        ))}
      </fieldset>

      <fieldset className="space-y-2 rounded-md border border-border/60 p-3">
        <legend className="px-1 text-xs font-medium text-muted-foreground">{limitsLegend}</legend>
        {catalog.limits.map((a) => {
          const isGlobal = a.fallback === "global";
          const g = isGlobal ? globalCap?.(a.key) : undefined;
          return (
            <div key={a.key} className="flex items-center justify-between gap-2 text-sm">
              <span title={a.description} className={cn(limitChanged(a.key) && "font-medium")}>
                {a.label}
                {limitChanged(a.key) && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-warning">
                    modificato
                  </span>
                )}
              </span>
              <div className="flex items-center gap-1.5">
                {limitChanged(a.key) && (
                  <ResetButton
                    label={a.label}
                    onClick={() =>
                      onChange({
                        ...value,
                        limits: { ...value.limits, [a.key]: baseline?.limits?.[a.key] ?? null },
                      })
                    }
                  />
                )}
                <div className="flex flex-col items-end">
                  <Input
                    type="number"
                    min={0}
                    aria-label={a.label}
                    className="h-8 w-28"
                    value={value.limits[a.key] ?? ""}
                    onChange={(e) => setLimit(a.key, e.target.value)}
                    placeholder={isGlobal ? (g != null ? `= globale: ${g}` : "globale") : "∞"}
                  />
                  {isGlobal && (
                    <span className="text-[10px] text-muted-foreground">vuoto = default globale</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </fieldset>
    </>
  );
}

function ResetButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={`Ripristina ${label}`}
      title="Torna al valore del piano"
      className="text-muted-foreground hover:text-foreground"
      onClick={onClick}
    >
      <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
    </button>
  );
}

/** Read-only rendering of the entitlements a licence actually grants. */
export function EntitlementsSummary({
  catalog,
  value,
}: {
  catalog: EntitlementsCatalog;
  value: PlanEntitlements;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Funzionalità
        </p>
        <div className="flex flex-wrap gap-1.5">
          {catalog.features.map((a) => {
            const on = Boolean(value.features?.[a.key]);
            return (
              <span
                key={a.key}
                title={a.description}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-xs",
                  on
                    ? "border-success/40 bg-success/10 text-foreground"
                    : "border-border bg-elevated text-muted-foreground line-through",
                )}
              >
                {a.label}
              </span>
            );
          })}
        </div>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Limiti
        </p>
        <dl className="space-y-1 text-sm">
          {catalog.limits.map((a) => {
            const v = value.limits?.[a.key] ?? null;
            return (
              <div key={a.key} className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground" title={a.description}>
                  {a.label}
                </dt>
                <dd className="tabular-nums">
                  {v === null
                    ? a.fallback === "global"
                      ? "globale"
                      : "illimitato"
                    : v.toLocaleString()}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </div>
  );
}
